import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import BlockRenderer from './blocks/BlockRenderer';

/**
 * PageBuilder V3 - Architecture Simple et Scalable
 * Charge une page avec ses blocs depuis la nouvelle API
 * 
 * ============================================================================
 * FIX V3 - Intégration Layout Container pour cohérence avec CmsBlocksRenderer
 * ============================================================================
 * 
 * PROBLÈME: Les blocs étaient rendus SANS container layout, créant une
 * différence visuelle avec les pages utilisant CmsBlocksRenderer.
 * 
 * SOLUTION: Appliquer le même layout container que CmsBlocksRenderer
 * mais SANS zones (car PageBuilder traite tous les blocs uniformément).
 */

// Configuration du layout - UNIFIED 1200px
const PAGE_LAYOUT_CONFIG = {
  maxWidth: '1200px',      // UNIFIÉ: Container global unique
  paddingX: '24px',
  paddingY: '24px',
  gap: '24px'
};

const PageBuilder = ({ pageId, isPublic = false, customHeader, customFooter, canvasBackground }) => {
  const [page, setPage] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPage();
  }, [pageId]);

  const loadPage = async () => {
    try {
      setLoading(true);
      let response;
      
      if (isPublic && pageId === 'page-landing') {
        // Public endpoint for landing page
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landing`);
        const data = await response.json();
        setPage(data);
        setBlocks(data.blocks || []);
      } else {
        // Authenticated endpoint for other pages
        response = await api.get(`/pages/${pageId}`);
        setPage(response.data);
        setBlocks(response.data.blocks || []);
      }
      
      setError(null);
    } catch (err) {
      console.error(`Error loading page ${pageId}:`, err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground">Erreur lors du chargement de la page.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={canvasBackground ? { backgroundColor: canvasBackground } : undefined}>
      {customHeader}

      {/* ============================================================
          LAYOUT CONTAINER - Cohérent avec CmsBlocksRenderer
          ============================================================ */}
      <div 
        className="page-builder-container"
        data-page-builder="v3"
        style={{
          maxWidth: PAGE_LAYOUT_CONFIG.maxWidth,
          marginLeft: 'auto',
          marginRight: 'auto',
          paddingLeft: PAGE_LAYOUT_CONFIG.paddingX,
          paddingRight: PAGE_LAYOUT_CONFIG.paddingX,
          paddingTop: PAGE_LAYOUT_CONFIG.paddingY,
          paddingBottom: PAGE_LAYOUT_CONFIG.paddingY,
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        {/* Container flex pour le gap entre blocs */}
        <div 
          className="page-builder-blocks"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: PAGE_LAYOUT_CONFIG.gap,
            width: '100%'
          }}
        >
          {blocks.map((block) => (
            <div 
              key={block.id}
              className="page-builder-block-wrapper"
              data-block-id={block.id}
              style={{ width: '100%' }}
            >
              <BlockRenderer block={block} />
            </div>
          ))}
        </div>
      </div>

      {customFooter}
    </div>
  );
};

export default PageBuilder;